import { Check, LockKeyhole } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import type { PublicCardSummary } from "../../shared/publicCard";
import { api } from "../api";
import { useAuth } from "../auth";
import { BoundaryCallout, Button, CopyButton, ExternalArrow, Modal, ScopeChoice } from "../components";

export type LoopChoice = { id: string; name: string; resolvedRuns: number };

type CreatedCard = { card: PublicCardSummary; token: string; url: string };

type LoopListResponse = { loops: Array<{ id: string; name: string; performance: { resolvedRuns: number } }> };

/**
 * Publishes a public card. `kind="loop"` takes the one loop it is opened for; `kind="status"` offers a
 * checklist of every loop visible in the workspace, with evaluable loops checked by default.
 */
export function ShareCardModal({ open, onClose, kind, loops, defaultTitle }: { open: boolean; onClose: () => void; kind: "loop" | "status"; loops?: LoopChoice[]; defaultTitle?: string }) {
  const { user, activeOrganization } = useAuth();
  const organizationId = activeOrganization?.id;
  const [title, setTitle] = useState("");
  const [showPurpose, setShowPurpose] = useState(false);
  const [choices, setChoices] = useState<LoopChoice[] | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [loadError, setLoadError] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [created, setCreated] = useState<CreatedCard | null>(null);

  useEffect(() => {
    if (!open) return;
    setTitle(defaultTitle ?? (kind === "loop" ? loops?.[0]?.name ?? "" : `${user?.displayName ?? "My"}'s loops`));
    setShowPurpose(false);
    setError("");
    setLoadError("");
    setCreated(null);
    if (loops) {
      setChoices(loops);
      setSelected(defaultSelection(kind, loops));
      return;
    }
    const controller = new AbortController();
    setChoices(null);
    void api<LoopListResponse>("/api/app/loops", { signal: controller.signal }, organizationId)
      .then((data) => {
        const list = data.loops.map((loop) => ({ id: loop.id, name: loop.name, resolvedRuns: loop.performance.resolvedRuns }));
        setChoices(list);
        setSelected(defaultSelection(kind, list));
      })
      .catch((caught: unknown) => {
        if (caught instanceof DOMException && caught.name === "AbortError") return;
        setLoadError(caught instanceof Error ? caught.message : "The loop list is unavailable.");
      });
    return () => controller.abort();
  }, [open, kind, loops, defaultTitle, organizationId, user?.displayName]);

  function toggle(id: string, checked: boolean) {
    setSelected((current) => (checked ? [...new Set([...current, id])] : current.filter((item) => item !== id)));
  }

  async function publish(event: FormEvent) {
    event.preventDefault();
    if (!organizationId) return;
    if (!selected.length) {
      setError("Choose at least one loop for the card.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const result = await api<CreatedCard>("/api/app/cards", { method: "POST", body: JSON.stringify({ kind, title: title.trim() || undefined, loopIds: selected, showPurpose }) }, organizationId);
      setCreated(result);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The card could not be published.");
    } finally {
      setBusy(false);
    }
  }

  const intent = created ? `https://twitter.com/intent/tweet?text=${encodeURIComponent(created.card.title)}&url=${encodeURIComponent(created.url)}` : "";

  return (
    <Modal open={open} title={kind === "loop" ? "Share this loop publicly" : "Share a status card"} onClose={onClose}>
      {created ? (
        <div className="share-card__result">
          <p className="share-card__ready" role="status"><Check size={15} aria-hidden="true" /> Card published. Anyone with the link can view it.</p>
          <img className="share-card__preview" src={`/og/${encodeURIComponent(created.token)}.png`} alt={`Preview of the card ${created.card.title}`} width={1200} height={630} />
          <div className="share-card__link"><code>{created.url}</code><CopyButton value={created.url} label="Copy link" /></div>
          <div className="modal__actions share-card__actions">
            <a className="button button--secondary" href={intent} target="_blank" rel="noreferrer noopener">Post on X <ExternalArrow /></a>
            <a className="button button--secondary" href={created.url} target="_blank" rel="noreferrer noopener">Open card <ExternalArrow /></a>
            <Button type="button" onClick={onClose}>Done</Button>
          </div>
          <p className="share-card__note"><LockKeyhole size={14} aria-hidden="true" /> The link is shown once. Revoke it any time in Settings under Public cards; run summaries, errors, cost, and keys are never included.</p>
        </div>
      ) : (
        <form className="form-stack share-card" onSubmit={(event) => void publish(event)}>
          <label><span>Card title</span><input value={title} onChange={(event) => setTitle(event.target.value)} placeholder={kind === "loop" ? "Loop name" : "Your loops"} maxLength={80} minLength={2} required /></label>
          {kind === "status" && (
            <fieldset className="share-card__choices">
              <legend>Loops on the card</legend>
              {choices === null && !loadError && <p className="share-card__hint">Reading the loops in this workspace…</p>}
              {loadError && <div className="form-error" role="alert">{loadError}</div>}
              {choices?.length === 0 && <p className="share-card__hint">No loops are visible in this workspace yet.</p>}
              {choices?.map((loop) => (
                <ScopeChoice key={loop.id} name="loopIds" label={loop.name} detail={loop.resolvedRuns >= 3 ? `${loop.resolvedRuns} resolved · reliability shown` : `${loop.resolvedRuns} of 3 resolved · reliability withheld`} checked={selected.includes(loop.id)} onChange={(checked) => toggle(loop.id, checked)} />
              ))}
            </fieldset>
          )}
          <ScopeChoice name="showPurpose" label="Include each loop's purpose" detail="Off by default. Descriptions stay private unless you turn this on." checked={showPurpose} onChange={setShowPurpose} />
          <BoundaryCallout title="What a public card shows" tone="info">Loop names, provider, cadence, evidence stage, reliability with its sample, and a seven-day rhythm. Never run summaries, errors, tokens, cost, or keys. The link can be revoked at any time.</BoundaryCallout>
          {error && <div className="form-error" role="alert">{error}</div>}
          <div className="modal__actions"><Button type="button" variant="ghost" onClick={onClose}>Cancel</Button><Button type="submit" busy={busy} disabled={kind === "status" && !choices?.length}>Publish card</Button></div>
        </form>
      )}
    </Modal>
  );
}

function defaultSelection(kind: "loop" | "status", loops: LoopChoice[]): string[] {
  if (kind === "loop") return loops.slice(0, 1).map((loop) => loop.id);
  const evaluable = loops.filter((loop) => loop.resolvedRuns >= 3).map((loop) => loop.id);
  return evaluable.length ? evaluable : loops.map((loop) => loop.id);
}
